import { Component, DestroyRef, ElementRef, inject, ViewChild, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';

// Third party
import { format } from 'date-fns';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxMaskDirective } from 'ngx-mask';
import { NgClass } from '@angular/common';
import { MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { OnlyNumbersDirective } from 'src/app/shared/directives/only-numbers';
// models
import {
  DateFormats,
  SirNames,
  StateList,
  DatePickerConfig,
  Questions,
  Answers,
  DispositionTypes,
  NoteTypes,
  State,
  CreationTypes,
  StatusModalIndicator,
  ModalID,
  Events,
} from '../../shared/models/constants';
// Services
import { StepProgressBarService } from '../../shared/components/step-progress-bar/step-progress-bar.service';
import { SharedService } from '../../shared/services/shared.service';

// functions
import {
  createNote,
  validateFflRegex,
  parseFflFirstThreeLastFive,
  parseFfleZCheckResponse,
  absolutifyFflEzUrls,
  parseFflEzResult,
  formatFfl,
  findStateByAbbreviation,
} from '../../shared/functions';
import { buildEventRecord } from '../../shared/utils/event-log.utils';
import {
  BoundBookEntry,
  FFL,
  Note,
  ReservedFirearm,
  UpdatedBoundBookResult,
} from '../../shared/models/general';
import { AtfFfl } from '../../shared/models/atf-ffl';
import { Subscription, take, finalize } from 'rxjs';
import { DriverLicense } from '../../shared/models/drivers-license-parser';
import { MatStepper } from '@angular/material/stepper';
import { NetworkSyncService } from 'src/app/shared/services/network-sync.service';

@Component({
  selector: 'app-disposition',
  templateUrl: './disposition.component.html',
  styleUrls: ['./disposition.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, BsDatepickerModule, NgxMaskDirective, NgClass, MatStepperModule, MatIconModule, OnlyNumbersDirective],
})
export class DispositionComponent {
  @ViewChild('stepper') stepper: MatStepper;
  private destroyRef = inject(DestroyRef);
  subscriptions = new Subscription();
  disposition_form: FormGroup;
  selected_dispo_type: DispositionTypes.individual | DispositionTypes.ffl =
    DispositionTypes.individual;
  states = StateList;
  disableNextBtn = false;
  filteredFirearms: any[] = [];
  sr_names = SirNames;
  form_4473_id_creation_type: CreationTypes.auto | CreationTypes.manual | null =
    null;
  ffl_verified: boolean = false;
  isProgrammaticNavigation: boolean = false;
  distinct_cities: string[] = [];

  bsConfig: Partial<BsDatepickerConfig>;
  constructor(
    private fb: FormBuilder,
    public sharedService: SharedService,
    public stepProgressBarService: StepProgressBarService,
    public networkSyncService: NetworkSyncService,
    private cdr: ChangeDetectorRef
  ) {
    this.disposition_form = this.fb.group({
      ffl: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      disposed_to: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      business_name: new FormControl({ value: null, disabled: false }),
      form_4473_no: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      first_name: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      middle_name: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      last_name: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      sr_name: new FormControl({ value: null, disabled: false }),

      address: new FormControl({ value: null, disabled: false }),
      address_2: new FormControl({ value: null, disabled: false }),
      zip_code: new FormControl({ value: null, disabled: false }),
      city: new FormControl({ value: null, disabled: false }),
      state: new FormControl({ value: null, disabled: false }),

      notes: new FormControl({ value: null, disabled: false }),
      dispo_date: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
    });
    // ffl_selected
    this.subscriptions.add(
      this.sharedService.ffl_selected$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: FFL) => {
          if (this.networkSyncService.isOnline) {
            this.sharedService.openStatusSpinner('Verifying FFL');
            const ffl_raw = parseFflFirstThreeLastFive(response.ffl);
            if (ffl_raw) {
              this.sharedService
                .getFflEzCheck(
                  ffl_raw.first,
                  ffl_raw.secondThird,
                  ffl_raw.lastFive,
                )
                .subscribe((ezResponse) => {
                  this.sharedService.closeStatusSpinner();
                  const isValid = parseFfleZCheckResponse(ezResponse);
                  if (isValid) {
                    this.ffl_verified = true;
                    this.patchFfl(response);
                  } else {
                    this.sharedService.raiseErrorToast(
                      'FFL could not be verified via ATF EZ Check',
                    );
                  }
                });
            } else {
              this.sharedService.closeStatusSpinner();
              this.sharedService.openYesNoModal(
                'Would you like to remove this FFL from FFL Management?',
              );
            }
          } else {
            this.patchFfl(response);
            this.ffl_verified = false;
          }
        }),
    );
    // yes_no_submit$
    this.subscriptions.add(
      this.sharedService.yes_no_submit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          // correct_ffl
          if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.submit_disposition
          ) {
            this.submitDisposition();
          } else if (
            response['answer'] === Answers.no &&
            response['question'] === Questions.form4473_id_already_exists
          ) {
            this.disposition_form.patchValue({
              form_4473_no: null,
            });
          } else if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.correct_ffl
          ) {
            response['optional_details']['ffl'] =
              this.disposition_form.get('ffl')?.value;
            let selected_ffl: FFL = response['optional_details'];
            this.ffl_verified = true;
            const short_ffl = parseFflFirstThreeLastFive(selected_ffl.ffl);
            this.sharedService
              .searchFflByShort(
                short_ffl.first,
                short_ffl.secondThird,
                short_ffl.lastFive,
              )
              .subscribe((response) => {
                selected_ffl.ffl = response.ffls[0].ffl_number;
                if (selected_ffl.ffl.length === 15) {
                  selected_ffl.ffl = formatFfl(selected_ffl.ffl);
                }
                this.patchFfl(selected_ffl);
                this.distinct_cities = [];
              });
          }
        }),
    );
    // drivers_license_id_selected$
    this.subscriptions.add(
      this.sharedService.drivers_license_id_selected$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((drivers_id: string) => {
          this.patchDriverLicenseValues(drivers_id);
        }),
    );
    // proceed_with_duplicate_ttsn$
    this.subscriptions.add(
      this.sharedService.proceed_with_duplicate_ttsn$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(async (response: boolean) => {
          if (response) {
            this.submitDisposition();
          } else {
            this.generate4473IdClick();
            this.sharedService.raiseInfoToast(
              'A new 4473 ID has been generated',
            );
          }
        }),
    );
    // this.bound_book_updated$
    this.subscriptions.add(
      this.sharedService.bound_book_updated$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: UpdatedBoundBookResult) => {
          this.sharedService.raiseInfoToast(
            'Items have been successfully disposed',
          );
          this.sharedService.changeRoute('bound-book');
        }),
    );
    // firearm_selected$
    this.subscriptions.add(
      this.sharedService.firearm_selected$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: BoundBookEntry) => {
          let reservation_firearm: ReservedFirearm[] = [];
          const reserved_firearm = new ReservedFirearm();
          reserved_firearm.bound_book_id =
            this.sharedService.selected_boundbook.boundbook_id;
          reserved_firearm.firearm_id = response.firearm_id;
          reserved_firearm.firearm =
            response.a_manufacture +
            ' - ' +
            response.a_model +
            ' - ' +
            response.a_caliber_gauge;
          reserved_firearm.serial_number = response.a_serial_number;
          reserved_firearm.reserved_date = '';
          reserved_firearm.reserved_to = '';
          reserved_firearm.reserved_by =
            this.sharedService.logged_in_user.username;
          reserved_firearm.notes = '';
          reservation_firearm.push(reserved_firearm);
          this.filteredFirearms = reservation_firearm;
        }),
    );
    // reserved_firearm_selected$
    this.subscriptions.add(
      this.sharedService.reserved_firearm_selected$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          console.log('[DISPO DEBUG] reserved_firearm_selected$ fired. response:', response);
          console.log('[DISPO DEBUG] selected_reservation_id BEFORE:', this.sharedService.selected_reservation_id);
          console.log('[DISPO DEBUG] reserved_firearm_list length:', this.sharedService.reserved_firearm_list.length);
          console.log('[DISPO DEBUG] reserved_firearm_list reservation_ids:', this.sharedService.reserved_firearm_list.map(f => f.reservation_id));
          if (response) {
            this.sharedService.selected_reservation_id = response;
          }
          this.filteredFirearms =
            this.sharedService.reserved_firearm_list.filter(
              (firearm) =>
                String(firearm.reservation_id) ===
                String(this.sharedService.selected_reservation_id),
            );
          console.log('[DISPO DEBUG] filteredFirearms after subscription filter:', this.filteredFirearms.length, this.filteredFirearms);
          this.selected_dispo_type = this.findDispositionType();

          this.isProgrammaticNavigation = true;
          setTimeout(() => {
            // Re-run filter just in case the shared service updated
            this.filteredFirearms =
              this.sharedService.reserved_firearm_list.filter(
                (firearm) =>
                  String(firearm.reservation_id) ===
                  String(this.sharedService.selected_reservation_id),
              );
            console.log('[DISPO DEBUG] filteredFirearms after setTimeout re-filter:', this.filteredFirearms.length);

            if (this.stepper && this.filteredFirearms.length > 0) {
              this.stepper.selectedIndex = 1;
              this.cdr.detectChanges();
              setTimeout(() => (this.isProgrammaticNavigation = false), 500);
            } else {
              this.isProgrammaticNavigation = false;
            }
          });
        }),
    );
    this.bsConfig = Object.assign({}, DatePickerConfig);
  }
  findDispositionType(): DispositionTypes.ffl | DispositionTypes.individual {
    const reserved_for = this.sharedService.reserved_firearm_list.find(
      (firearm) =>
        String(firearm.reservation_id) ===
        String(this.sharedService.selected_reservation_id),
    )?.reserved_to;
    if (reserved_for === 'Individual') {
      return DispositionTypes.individual;
    }
    return DispositionTypes.ffl;
  }
  async ngOnInit() {
    this.sharedService.loading = false;
    console.log('[DISPO DEBUG] ngOnInit. selected_reservation_id:', this.sharedService.selected_reservation_id);
    console.log('[DISPO DEBUG] ngOnInit. reserved_firearm_list length:', this.sharedService.reserved_firearm_list.length);
    console.log('[DISPO DEBUG] ngOnInit. reserved_firearm_list:', JSON.stringify(this.sharedService.reserved_firearm_list.map(f => ({id: f.reservation_id, firearm: f.firearm, serial: f.serial_number}))));
    if (!this.sharedService.selected_reservation_id) {
      this.sharedService.changeRoute('home');
      return;
    }
    this.patchDispositionDate();
    this.filteredFirearms = this.sharedService.reserved_firearm_list.filter(
      (firearm) =>
        String(firearm.reservation_id) ===
        String(this.sharedService.selected_reservation_id),
    );
    console.log('[DISPO DEBUG] ngOnInit filteredFirearms:', this.filteredFirearms.length, this.filteredFirearms);
    this.selected_dispo_type = this.findDispositionType();
  }
  ngAfterViewInit() {
    // Navigating here causes a snapback if data isn't ready.
    // However, if we arrived here with data already set (e.g. from Disposition Selection), we need to navigate.
    if (this.stepper && this.filteredFirearms.length > 0) {
      setTimeout(() => {
        this.isProgrammaticNavigation = true;
        this.stepper.selectedIndex = 1;
        this.cdr.detectChanges();
        setTimeout(() => (this.isProgrammaticNavigation = false), 500);
      });
    }
  }
  patchDriverLicenseValues(drivers_id: string) {
    const driver_license: DriverLicense = this.findDriverLicense(drivers_id);
    const state = this.states.filter(
      (state) => state.state_code === driver_license.addressState,
    )[0].state_name;
    this.disposition_form.patchValue({
      first_name: driver_license.nameFirst,
      middle_name: driver_license.nameMiddle,
      last_name: driver_license.nameLast,
      sr_name: null, // driver_license.sr_name,
      address: driver_license.addressStreet,
      address_2: null,
      zip_code: driver_license.addressPostalCode,
      city: driver_license.addressCity,
      state: state,
    });
  }
  findDriverLicense(drivers_id: string): DriverLicense {
    return this.sharedService.recent_drivers_license.filter(
      (license) => license.driversId === drivers_id,
    )[0];
  }
  // Button Actions
  previousStepClick() {
    if (this.stepper.selectedIndex >= 1) {
      this.stepper.previous();
    }
    if (this.stepper.selectedIndex === 0) {
      this.sharedService.selected_reservation_id = null;
      this.sharedService.changeRoute('reserved-firearms');
    }
  }
  nextStepClick() {
    this.enableNextBtn();
  }
  async submitDispositionClick() {
    //this.sharedService.openYesNoModal(Questions.submit_disposition);
    if (this.selected_dispo_type === DispositionTypes.individual) {
      await this.verify4473();
      this.submitDisposition();
      this.sharedService.changeRoute('bound-book');
      this.sharedService.deleteFirearmFromReserveList(
        this.sharedService.selected_reservation_id,
      );
    } else {
      this.submitDisposition();
      this.sharedService.changeRoute('bound-book');
      this.sharedService.deleteFirearmFromReserveList(
        this.sharedService.selected_reservation_id,
      );
    }
  }
  fflSearchClick() {
    this.sharedService.openFflSearchModal();
  }
  selectDriversLicenseClick() {
    this.sharedService.openIdSelectionModal();
  }
  async generate4473IdClick() {
    const result = await this.sharedService.generate4473Id();
    this.disposition_form.patchValue({
      form_4473_no: result,
    });
    this.form_4473_id_creation_type = CreationTypes.auto;
  }
  async submitDisposition() {
    await this.updateCorrespondingBoundBookEntry();
    await this.removeItemsFromReservedList();
    this.clearUnusedFields();
  }
  async updateCorrespondingBoundBookEntry() {
    for (const firearm of this.filteredFirearms) {
      let bound_book_entry = this.sharedService.bound_book.filter(
        (entry) => +entry.firearm_id === +firearm.firearm_id,
      )[0];
      if (this.selected_dispo_type === DispositionTypes.individual) {
        bound_book_entry.d_form4473_id =
          this.disposition_form.get('form_4473_no').value;
        bound_book_entry.d_first_name =
          this.disposition_form.get('first_name').value;
        bound_book_entry.d_middle_name =
          this.disposition_form.get('middle_name').value;
        bound_book_entry.d_last_name =
          this.disposition_form.get('last_name').value;
        bound_book_entry.d_sr_name = this.disposition_form.get('sr_name').value;
        bound_book_entry.d_address = this.disposition_form.get('address').value;
        bound_book_entry.d_address2 =
          this.disposition_form.get('address_2').value;
        bound_book_entry.d_zip_code =
          this.disposition_form.get('zip_code').value;
        bound_book_entry.d_city = this.disposition_form.get('city').value;
        bound_book_entry.d_state = this.disposition_form.get('state').value;
        bound_book_entry.d_date_disposed =
          this.disposition_form.get('dispo_date').value;
      } else {
        bound_book_entry.d_ffl = this.disposition_form.get('ffl').value;
        bound_book_entry.d_disposed_to =
          this.disposition_form.get('disposed_to').value;
        bound_book_entry.d_business_name =
          this.disposition_form.get('business_name').value;
        bound_book_entry.d_date_disposed =
          this.disposition_form.get('dispo_date').value;
      }
      const note: Note = createNote(
        this.disposition_form.get('notes').value,
        NoteTypes.disposition_creation,
        this.sharedService,
      );
      bound_book_entry.notes.push(note);
      
      const to_values: any = {};
      if (this.selected_dispo_type === DispositionTypes.individual) {
        to_values.d_form4473_id = this.disposition_form.get('form_4473_no').value;
        to_values.d_first_name = this.disposition_form.get('first_name').value;
        to_values.d_last_name = this.disposition_form.get('last_name').value;
      } else {
        to_values.d_ffl = this.disposition_form.get('ffl').value;
        to_values.d_disposed_to = this.disposition_form.get('disposed_to').value;
      }
      to_values.d_date_disposed = this.disposition_form.get('dispo_date').value;

      bound_book_entry.event_log.push(
        buildEventRecord(
          this.sharedService,
          Events.create_disposition,
          null,
          to_values
        )
      );

      bound_book_entry.state = State.disposition;
      await this.sharedService.updateBoundBookEntry(bound_book_entry);
    }
  }
  // General Functions
  clearUnusedFields() {
    if (this.selected_dispo_type === DispositionTypes.individual) {
      this.disposition_form.patchValue({
        first_name: null,
        middle_name: null,
        last_name: null,
        sr_name: null,
        address: null,
        address_2: null,
        zip_code: null,
        city: null,
        state: null,
      });
    } else if (this.selected_dispo_type === DispositionTypes.ffl) {
      this.disposition_form.patchValue({
        ffl: null,
        disposed_to: null,
        business_name: null,
      });
    }
  }
  patchDispositionDate() {
    this.disposition_form.patchValue({
      dispo_date: format(new Date(), DateFormats.M_D_Y),
    });
  }
  isStep0Complete(): boolean {
    return this.filteredFirearms.length > 0;
  }
  isStepValid(stepIndex: number): boolean {
    if (stepIndex === 0) {
      return this.filteredFirearms.length > 0;
    }
    if (stepIndex === 1) {
      const form = this.disposition_form;
      if (this.selected_dispo_type === DispositionTypes.individual) {
        return (
          form.get('form_4473_no').valid &&
          form.get('first_name').valid &&
          form.get('middle_name').valid &&
          form.get('last_name').valid &&
          form.get('dispo_date').valid
        );
      } else {
        return (
          form.get('ffl').valid &&
          form.get('disposed_to').valid &&
          form.get('dispo_date').valid
        );
      }
    }
    if (stepIndex === 2) {
      return this.disposition_form.get('notes').valid;
    }
    return true;
  }
  enableNextBtn(event?: StepperSelectionEvent) {
    if (this.isProgrammaticNavigation) {
      return;
    }
    if (event) {
      if (event.selectedIndex > event.previouslySelectedIndex) {
        const isValid = this.isStepValid(event.previouslySelectedIndex);
        if (!isValid) {
          setTimeout(() => {
            this.stepper.selectedIndex = event.previouslySelectedIndex;
          });
          this.sharedService.raiseErrorToast(
            'Please fill out all required fields 1',
          );
        }
      }
      return;
    }
    if (this.isStepValid(this.stepper.selectedIndex)) {
      this.stepper.next();
    } else {
      this.sharedService.raiseErrorToast(
        'Please fill out all required fields 2',
      );
    }
  }
  fetchFfl() {
    const fflValue = this.disposition_form.get('ffl').value;

    // If the value is empty, or if it was previously verified and the user altered it (length < 20)
    if (
      !fflValue ||
      fflValue.trim() === '' ||
      (this.ffl_verified && fflValue.length < 20)
    ) {
      this.clearFflFields();
      if (!fflValue || fflValue.trim() === '') {
        return;
      }
    }

    if (
      fflValue &&
      fflValue.length === 20 &&
      this.networkSyncService.isOnline
    ) {
      const ffl = this.disposition_form.get('ffl').value.replace(/-/g, '');
      const licsRegn = ffl.charAt(0);
      const licsDis = ffl.substring(1, 3);
      const licsSeq = ffl.slice(-5);
      this.sharedService.openStatusSpinner('Fetching FFL details');
      this.sharedService
        .getFflEzCheck(licsRegn, licsDis, licsSeq)
        .pipe(
          finalize(() => {
            this.sharedService.closeStatusSpinner();
          }),
        )
        .subscribe({
          next: (response) => {
            setTimeout(() => {
              const fixedHtml = absolutifyFflEzUrls(response);
              const parsedFfl = parseFflEzResult(fixedHtml);
              if (parsedFfl.ffl_number.length !== 0) {
                const fetched_ffl: FFL = new FFL();
                fetched_ffl.ffl = parsedFfl.ffl_number.toString();
                fetched_ffl.legal_name = parsedFfl?.license_name.toString();
                fetched_ffl.business_name = parsedFfl.business_name.toString();
                fetched_ffl.premise_street =
                  parsedFfl.premise_street.toString();
                fetched_ffl.premise_city = parsedFfl.premise_city.toString();
                fetched_ffl.premise_state = parsedFfl.premise_state.toString();
                fetched_ffl.premise_zip_code =
                  parsedFfl.premise_zip_code.toString();
                fetched_ffl.phone_number = null;
                fetched_ffl.email = null;
                fetched_ffl.notes = null;
                fetched_ffl.issue_date = parsedFfl.loa_issue_date.toString()
                  ? parsedFfl.loa_issue_date.toString()
                  : null;
                fetched_ffl.expiration_date =
                  parsedFfl.loa_expiration_date.toString()
                    ? parsedFfl.loa_expiration_date.toString()
                    : null;
                this.sharedService.openYesNoModal(
                  Questions.correct_ffl,
                  fetched_ffl,
                  true,
                );
              } else {
                this.sharedService.openMessageModal(
                  'Please verify your entry as no FFL was found.',
                );
              }
            }, 500);
          },
          error: () => {
            this.sharedService.openMessageModal(
              'Failed to fetch FFL details. Please try again.',
            );
          },
        });
    }
  }
  patchFfl(response_details: FFL) {
    this.disposition_form.patchValue({
      ffl: response_details.ffl,
      disposed_to: response_details.legal_name,
      business_name: response_details.business_name,
      address: response_details.premise_street,
      zip_code: response_details.premise_zip_code,
      city: response_details.premise_city,
      state: findStateByAbbreviation(response_details.premise_state),
    });
    const businessNameControl = this.disposition_form.get('business_name');
    businessNameControl.markAsDirty();
    businessNameControl.markAsTouched();
    businessNameControl.updateValueAndValidity();
  }
  clearFflFields() {
    this.disposition_form.patchValue({
      disposed_to: null,
      business_name: null,
      address: null,
      zip_code: null,
      city: null,
      state: null,
    });
    this.ffl_verified = false;
  }
  fetchCityState(): void {
    const zipcode = this.disposition_form.get('zip_code').value;
    if (zipcode.length === 5) {
      this.sharedService
        .getCityState(zipcode)
        .then((response) => {
          if (response) {
            this.distinct_cities = response.cities;
            const state = this.states.filter(
              (state) => state.state_code === response.state,
            )[0]?.state_name;

            const city =
              this.distinct_cities.length === 1 ? this.distinct_cities[0] : null;

            // Update form controls with city and state
            this.disposition_form.patchValue({
              city: city,
              state: state,
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching city and state:', error);
        });
    }
  }
  async verify4473() {
    const form_4473_no: string =
      this.disposition_form.get('form_4473_no').value;
    // Skip duplicate check when offline — the transfer number is blank and will
    // be assigned server-side at sync time in chronological order.
    if (!form_4473_no || !this.sharedService.networkSync.isOnline) return;
    const alreadyExists = await this.sharedService.check4473Value(form_4473_no);
    if (alreadyExists.exists === true) {
      this.sharedService.openDuplicateTTSNSelectionModal(form_4473_no);
    }
  }
  changeDispoType(): void {
    if (this.selected_dispo_type === DispositionTypes.individual) {
      this.selected_dispo_type = DispositionTypes.ffl;
    } else if (this.selected_dispo_type === DispositionTypes.ffl) {
      this.selected_dispo_type = DispositionTypes.individual;
    }
  }
  setForm4473IdManual() {
    this.form_4473_id_creation_type = CreationTypes.manual;
  }
  async removeItemsFromReservedList() {
    for (const firearm of this.filteredFirearms) {
      let reservation_id = this.sharedService.reserved_firearm_list.filter(
        (entry) => +entry.firearm_id === +firearm.firearm_id,
      )[0].reservation_id;
      try {
        await this.sharedService.deleteFirearmFromReserveList(reservation_id);
      } catch (error) {
        this.sharedService.raiseErrorToast(
          'Failed to remove firearm from reserve list.',
        );
      }
    }
  }
  openHome() {
    this.sharedService.changeRoute('home');
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
